import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, SelectItem, Switch } from '@heroui/react';
import AirtablePrimitiveConfigurator from './AirtablePrimitiveConfigurator.jsx';
import GoogleDocsPrimitiveConfigurator from './GoogleDocsPrimitiveConfigurator.jsx';
import AhrefsFirehosePrimitiveConfigurator from './AhrefsFirehosePrimitiveConfigurator.jsx';
import ExternalPrimitiveMappingsEditor from './ExternalPrimitiveMappingsEditor.jsx';

export default function ExternalPrimitiveConfigurator({ primitive, provider, loadingProviders, integrationProviders }) {
  const resolvedConfig = primitive?.getConfig ?? primitive?.referenceParameters ?? {};
  const providerName = resolvedConfig?.provider ?? primitive?.referenceParameters?.provider;
  const accountId = primitive?.referenceParameters?.integrationAccountId
    ?? resolvedConfig?.integrationAccountId
    ?? primitive?.resources?.integration?.accountId
    ?? primitive?.referenceParameters?.accountId;
  const requiresAccount = Boolean(providerName && provider?.authType !== 'none' && providerName !== 'ahrefs');

  const primitiveFields = provider?.configuration?.primitive ?? [];
  const hasDedicatedConfigurator = ['airtable', 'google-docs', 'ahrefs'].includes(providerName);

  const providerSupportsPrimitiveConfig = primitiveFields.length > 0 || hasDedicatedConfigurator;
  const providerLoading = loadingProviders || (providerName && provider === undefined);

  // Account selection state
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectingAccount, setSelectingAccount] = useState(false);

  useEffect(() => {
    if (requiresAccount && providerName && !accountId) {
      setLoadingAccounts(true);
      const workspaceId = primitive?.workspaceId ?? primitive?.referenceParameters?.workspaceId;
      const params = new URLSearchParams({ provider: providerName });
      if (workspaceId) {
        params.set('workspaceId', workspaceId);
      }
      fetch(`/api/integrations/accounts?${params}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load integration accounts (${response.status})`);
          }
          return response.json();
        })
        .then((body) => {
          setAccounts(Array.isArray(body?.accounts) ? body.accounts : []);
        })
        .catch((error) => {
          console.error(error);
          setAccounts([]);
        })
        .finally(() => setLoadingAccounts(false));
    } else if (!requiresAccount || accountId) {
      setAccounts([]);
    }
  }, [requiresAccount, providerName, accountId, primitive?.workspaceId, primitive?.referenceParameters?.workspaceId]);

  const handleAccountSelect = useCallback(async (selectedId) => {
    if (!selectedId || !primitive?.setField) return;
    try {
      setSelectingAccount(true);
      await primitive.setField('referenceParameters.integrationAccountId', selectedId);
      console.info(`Integration account "${selectedId}" linked to external primitive`);
    } catch (error) {
      console.error('Failed to link integration account to external primitive', error);
    } finally {
      setSelectingAccount(false);
    }
  }, [primitive]);

  const deriveAvailableFields = useCallback((targetPrimitive) => {
    const source = targetPrimitive?.referenceParameters?.source ?? {};
    if (Array.isArray(source.tableFields) && source.tableFields.length > 0) {
      return source.tableFields;
    }
    if (source.fields && typeof source.fields === 'object') {
      return Object.entries(source.fields).map(([name, details]) => ({
        id: name,
        name: details?.name ?? name,
        type: details?.type,
      }));
    }
    const sample = targetPrimitive?.referenceParameters?.sampleRecord?.fields;
    if (sample && typeof sample === 'object') {
      return Object.keys(sample).map((name) => ({ id: name, name }));
    }
    return [];
  }, []);

  const [availableFields, setAvailableFields] = useState(() => deriveAvailableFields(primitive));
  const deriveAvailableAttachments = useCallback((targetPrimitive) => {
    const sources = new Set();
    const direct = targetPrimitive?.referenceParameters?.availableAttachmentSources;
    if (Array.isArray(direct)) {
      direct.forEach((entry) => {
        if (!entry) return;
        sources.add(String(entry));
      });
    }
    const sampleAttachments = targetPrimitive?.referenceParameters?.sampleRecord?.attachments;
    if (sampleAttachments && typeof sampleAttachments === 'object') {
      Object.keys(sampleAttachments).forEach((key) => sources.add(String(key)));
    }
    const integrationAttachments = targetPrimitive?.referenceParameters?.integrationAttachments?.attachments;
    if (integrationAttachments && typeof integrationAttachments === 'object') {
      Object.keys(integrationAttachments).forEach((key) => sources.add(String(key)));
    }
    const googleDocsMeta = targetPrimitive?.referenceParameters?.googleDocs;
    if (googleDocsMeta && googleDocsMeta.fileName) {
      ['textContent', 'pdfBuffer', 'fileBuffer', 'fileContentType'].forEach((key) => sources.add(key));
    }
    // Fallback: if sampleRecord has Google Docs fields, include standard attachment keys
    if (sources.size === 0) {
      const sampleFields = targetPrimitive?.referenceParameters?.sampleRecord?.fields;
      if (sampleFields && (sampleFields.mimeType || sampleFields.webViewLink)) {
        ['textContent', 'pdfBuffer', 'fileBuffer', 'fileContentType', 'fileName'].forEach((key) => sources.add(key));
      }
    }
    return Array.from(sources);
  }, []);

  const [availableAttachmentSources, setAvailableAttachmentSources] = useState(
    () => deriveAvailableAttachments(primitive),
  );

  useEffect(() => {
    setAvailableFields(deriveAvailableFields(primitive));
    setAvailableAttachmentSources(deriveAvailableAttachments(primitive));
  }, [deriveAvailableAttachments, deriveAvailableFields, primitive?.referenceParameters]);

  const handleFieldsPreview = useCallback((fields) => {
    setAvailableFields(Array.isArray(fields) ? fields : []);
  }, []);

  const [selectingProvider, setSelectingProvider] = useState(false);

  const handleProviderSelect = useCallback(async (selectedName) => {
    if (!selectedName || !primitive?.setField) return;
    try {
      setSelectingProvider(true);
      await primitive.setField('referenceParameters.provider', selectedName);
      console.info(`Provider set to "${selectedName}" for external primitive`);
    } catch (error) {
      console.error('Failed to set provider on external primitive', error);
    } finally {
      setSelectingProvider(false);
    }
  }, [primitive]);

  const configurator = useMemo(() => {
    if (!providerName || !providerSupportsPrimitiveConfig) {
      return null;
    }

    switch (providerName) {
      case 'airtable':
        return (
          <AirtablePrimitiveConfigurator
            primitive={primitive}
            accountId={accountId}
            onFieldsPreview={handleFieldsPreview}
          />
        );
      case 'google-docs':
        return (
          <GoogleDocsPrimitiveConfigurator
            primitive={primitive}
            accountId={accountId}
          />
        );
      case 'ahrefs':
        return (
          <AhrefsFirehosePrimitiveConfigurator
            primitive={primitive}
          />
        );
      default:
        return null;
    }
  }, [accountId, handleFieldsPreview, primitive, providerName, providerSupportsPrimitiveConfig]);

  const providerSection = useMemo(() => {
    if (providerLoading && !provider) {
      return (
        <p className="mt-3 text-sm text-default-500">Loading provider details…</p>
      );
    }

    if (!providerName) {
      const availableProviders = Array.isArray(integrationProviders) ? integrationProviders : [];
      return (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-default-500">
            Select a provider for this external primitive to start configuring it.
          </p>
          <Select
            label="Integration provider"
            placeholder="Choose a provider..."
            className="max-w-xs"
            isDisabled={selectingProvider || availableProviders.length === 0}
            onSelectionChange={(value) => {
              const name = typeof value === 'string' ? value : Array.from(value || [])[0];
              if (name) {
                handleProviderSelect(name);
              }
            }}
          >
            {availableProviders.length === 0 && (
              <SelectItem key="_empty" textValue="No providers available" isDisabled>
                No providers available
              </SelectItem>
            )}
            {availableProviders.map((p) => (
              <SelectItem key={p.name} textValue={p.title || p.name}>
                {p.title || p.name}
              </SelectItem>
            ))}
          </Select>
          {selectingProvider && (
            <p className="text-xs text-default-400">Saving provider selection…</p>
          )}
        </div>
      );
    }

    if (!provider) {
      return (
        <p className="mt-3 text-sm text-danger-500">
          Provider "{providerName}" is not registered. Please reconnect the integration.
        </p>
      );
    }

    if (!providerSupportsPrimitiveConfig) {
      return (
        <p className="mt-3 text-sm text-default-500">
          {provider.title || provider.name} does not require additional configuration for each primitive.
        </p>
      );
    }

    if (requiresAccount && !accountId) {
      const availableAccounts = Array.isArray(accounts) ? accounts : [];
      return (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-warning-500">
            Connect this primitive to an integration account before configuring it.
          </p>
          <Select
            label="Integration account"
            placeholder="Choose an account..."
            className="max-w-xs"
            isDisabled={selectingAccount || loadingAccounts || availableAccounts.length === 0}
            onSelectionChange={(value) => {
              const id = typeof value === 'string' ? value : Array.from(value || [])[0];
              if (id) {
                handleAccountSelect(id);
              }
            }}
          >
            {loadingAccounts && (
              <SelectItem key="_loading" textValue="Loading..." isDisabled>
                Loading accounts…
              </SelectItem>
            )}
            {availableAccounts.length === 0 && !loadingAccounts && (
              <SelectItem key="_empty" textValue="No accounts available" isDisabled>
                No accounts available — connect one at /integrations first
              </SelectItem>
            )}
            {availableAccounts.map((a) => (
              <SelectItem key={a.id} textValue={`${a.provider} (${a.id})`}>
                {a.provider} — {a.id}
              </SelectItem>
            ))}
          </Select>
          {selectingAccount && (
            <p className="text-xs text-default-400">Linking account…</p>
          )}
        </div>
      );
    }

    if (!configurator) {
      return (
        <p className="mt-3 text-sm text-default-500">
          Configuration UI for {provider.title || provider.name} is not yet available.
        </p>
      );
    }

    return configurator;
  }, [
    accountId,
    accounts,
    configurator,
    handleAccountSelect,
    handleProviderSelect,
    integrationProviders,
    loadingAccounts,
    providerLoading,
    provider,
    providerName,
    providerSupportsPrimitiveConfig,
    requiresAccount,
    selectingAccount,
    selectingProvider,
  ]);

  const forwardSearchEnabled = Boolean(resolvedConfig?.forwardProjectSearch);

  const handleForwardSearchToggle = useCallback(async (checked) => {
    if (!primitive?.setField) return;
    try {
      await primitive.setField('referenceParameters.forwardProjectSearch', checked || null);
    } catch (error) {
      console.error('Failed to update forwardProjectSearch', error);
    }
  }, [primitive]);

  return (
    <div className="space-y-6">
      {providerSection}
      <div className="rounded-lg border border-default-200 bg-default-50 p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Forward project search</p>
            <p className="text-xs text-default-500">
              When new records or mapping children are added, automatically discover downstream flows and trigger re-scaffolding.
            </p>
          </div>
          <Switch
            isSelected={forwardSearchEnabled}
            onValueChange={handleForwardSearchToggle}
            size="sm"
          />
        </div>
      </div>
      <ExternalPrimitiveMappingsEditor
        primitive={primitive}
        availableFields={availableFields}
        availableAttachmentSources={availableAttachmentSources}
      />
    </div>
  );
}
